import React, { useState, useRef, useCallback, useEffect } from "react";
import { Graphics, Text, Container } from "@pixi/react";

function calculatePolygonArea(vertices) {
  let area = 0;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    area += (vertices[j].x + vertices[i].x) * (vertices[j].y - vertices[i].y);
  }
  return Math.abs(area / 2) / 10000; // px^2 to m^2
}

function getPolygonBounds(vertices) {
  const minX = Math.min(...vertices.map(v => v.x));
  const minY = Math.min(...vertices.map(v => v.y));
  const maxX = Math.max(...vertices.map(v => v.x));
  const maxY = Math.max(...vertices.map(v => v.y));
  return { minX, minY, maxX, maxY, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2, width: maxX - minX, height: maxY - minY };
}

// Draggable vertex node for polygon editing
const PolygonVertexNode = ({ x, y, index, onDrag }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  const handleMouseDown = (event) => {
    event.stopPropagation();
    setIsDragging(true);
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      globalPoint = { x: event.clientX, y: event.clientY };
    }
    setDragStart({ x: globalPoint.x - x, y: globalPoint.y - y });
  };

  const handleMouseMove = (event) => {
    if (!isDragging || !dragStart) return;
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      globalPoint = { x: event.clientX, y: event.clientY };
    }
    const newX = globalPoint.x - dragStart.x;
    const newY = globalPoint.y - dragStart.y;
    onDrag(index, { x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        const globalPoint = { x: event.clientX, y: event.clientY };
        const newX = globalPoint.x - dragStart.x;
        const newY = globalPoint.y - dragStart.y;
        onDrag(index, { x: newX, y: newY });
      };
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
      };
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, index, onDrag]);

  return (
    <Graphics
      x={x}
      y={y}
      draw={g => {
        g.clear();
        g.lineStyle(2, 0xffffff);
        g.beginFill(isHovered ? 0xff0000 : 0x00ff00);
        g.drawCircle(0, 0, 8);
        g.endFill();
      }}
      interactive={true}
      cursor="pointer"
      pointerdown={handleMouseDown}
      pointermove={handleMouseMove}
      pointerup={handleMouseUp}
      pointerover={() => setIsHovered(true)}
      pointerout={() => setIsHovered(false)}
      zIndex={1000}
    />
  );
};

// Draggable curve control for polygon side
const PolygonCurveControl = ({ start, end, control, sideIndex, onDrag }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  // Default control point: perpendicular to side, 40px out
  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const dx = end.x - start.x, dy = end.y - start.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const perp = { x: -dy / len, y: dx / len };
  const defaultControl = control || { x: mid.x + perp.x * 40, y: mid.y + perp.y * 40 };

  const handleMouseDown = (event) => {
    event.stopPropagation();
    setIsDragging(true);
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      globalPoint = { x: event.clientX, y: event.clientY };
    }
    setDragStart({ x: globalPoint.x - defaultControl.x, y: globalPoint.y - defaultControl.y });
  };

  const handleMouseMove = (event) => {
    if (!isDragging || !dragStart) return;
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      globalPoint = { x: event.clientX, y: event.clientY };
    }
    const newX = globalPoint.x - dragStart.x;
    const newY = globalPoint.y - dragStart.y;
    onDrag(sideIndex, { x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        const globalPoint = { x: event.clientX, y: event.clientY };
        const newX = globalPoint.x - dragStart.x;
        const newY = globalPoint.y - dragStart.y;
        onDrag(sideIndex, { x: newX, y: newY });
      };
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
      };
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, sideIndex, onDrag]);

  return (
    <Graphics
      x={defaultControl.x}
      y={defaultControl.y}
      draw={g => {
        g.clear();
        g.lineStyle(1, 0xffffff);
        g.beginFill(isHovered ? 0xff8f8f : 0xff6b6b);
        g.drawRect(-5, -5, 10, 10);
        g.endFill();
      }}
      interactive={true}
      cursor="move"
      pointerdown={handleMouseDown}
      pointermove={handleMouseMove}
      pointerup={handleMouseUp}
      pointerover={() => setIsHovered(true)}
      pointerout={() => setIsHovered(false)}
      zIndex={1000}
    />
  );
};

const PolygonRoom = ({ shape, isSelected, onSelect, isModifierMode = false, selectedMeasurementUnit = { abbreviation: "m", conversionFactor: 1 } }) => {
  const { vertices = [], fillColor = 0xe3f2fd, name = "Room", pattern, flooringPattern, highlightColor, showSquareFootage, showInsideDimensions, showOutsideDimensions } = shape;
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const containerRef = useRef();

  const bounds = getPolygonBounds(vertices);
  const area = calculatePolygonArea(vertices);

  // Drag logic
  const handleMouseDown = useCallback((event) => {
    event.stopPropagation();
    onSelect && onSelect();
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else if (event.clientX !== undefined) {
      const rect = event.target.getBoundingClientRect();
      globalPoint = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    } else {
      return;
    }
    setIsDragging(true);
    setDragStart({ x: globalPoint.x - bounds.minX, y: globalPoint.y - bounds.minY });
    window.dispatchEvent(new CustomEvent('shapeDragStart', { detail: { shapeId: shape.id } }));
    window.dispatchEvent(new CustomEvent('propogation-block', { detail: { isBlocked: false } }));
  }, [onSelect, shape.id, bounds.minX, bounds.minY]);

  const handleMouseMove = useCallback((event) => {
    if (!isDragging || !dragStart) return;
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else if (event.clientX !== undefined) {
      const rect = event.target.getBoundingClientRect();
      globalPoint = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    } else {
      return;
    }
    const dx = globalPoint.x - dragStart.x;
    const dy = globalPoint.y - dragStart.y;
    const newVertices = vertices.map(v => ({ x: v.x + (dx - bounds.minX), y: v.y + (dy - bounds.minY) }));
    if (typeof shape.onUpdate === 'function') {
      shape.onUpdate({ id: shape.id, vertices: newVertices });
    }
    window.dispatchEvent(new CustomEvent('shapeDragUpdate', { detail: { shapeId: shape.id, position: { x: dx, y: dy }, boundingBox: { x: dx, y: dy, width: bounds.width, height: bounds.height } } }));
  }, [isDragging, dragStart, vertices, shape, bounds.minX, bounds.minY, bounds.width, bounds.height]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
      window.dispatchEvent(new CustomEvent('shapeDragEnd', { detail: { shapeId: shape.id } }));
  }, [shape.id]);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        if (!dragStart) return;
        const globalPoint = { x: event.clientX, y: event.clientY };
        const dx = globalPoint.x - dragStart.x;
        const dy = globalPoint.y - dragStart.y;
        const newVertices = vertices.map(v => ({ x: v.x + (dx - bounds.minX), y: v.y + (dy - bounds.minY) }));
        if (typeof shape.onUpdate === 'function') {
          shape.onUpdate({ id: shape.id, vertices: newVertices });
        }
      };
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
      };
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, vertices, shape, bounds.minX, bounds.minY]);

  // Name editing handler
  const handleNameClick = (event) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent('showRectangleNameModal', {
      detail: {
        rectangleId: shape.id,
        currentName: name,
      },
    }));
  };

  // Flooring pattern drawing (simple version, can be extended)
  const drawFlooringPattern = useCallback((g) => {
    if (!vertices.length) return;
    g.clear();
    if (flooringPattern && flooringPattern.color) {
      g.beginFill(flooringPattern.color, 0.3);
    } else {
      g.beginFill(fillColor, 0.3);
    }
    g.moveTo(vertices[0].x, vertices[0].y);
    for (let i = 1; i < vertices.length; i++) {
      g.lineTo(vertices[i].x, vertices[i].y);
    }
    g.lineTo(vertices[0].x, vertices[0].y);
    g.endFill();
    // Optionally, add pattern lines here
  }, [vertices, fillColor, flooringPattern]);

  // Highlight overlay
  const drawHighlight = useCallback((g) => {
    if (!vertices.length || !highlightColor) return;
    g.clear();
    g.beginFill(highlightColor.hex || highlightColor, 0.2);
    g.moveTo(vertices[0].x, vertices[0].y);
    for (let i = 1; i < vertices.length; i++) {
      g.lineTo(vertices[i].x, vertices[i].y);
    }
    g.lineTo(vertices[0].x, vertices[0].y);
    g.endFill();
  }, [vertices, highlightColor]);

  // Main polygon fill
  const draw = React.useCallback(
    (g) => {
      if (!vertices.length) return;
      g.clear();
      g.beginFill(fillColor, isSelected ? 0.5 : 0.3);
      g.lineStyle(isSelected ? 3 : 1, 0x1976d2);
      g.moveTo(vertices[0].x, vertices[0].y);
      for (let i = 1; i < vertices.length; i++) {
        g.lineTo(vertices[i].x, vertices[i].y);
      }
      g.lineTo(vertices[0].x, vertices[0].y);
      g.endFill();
    },
    [vertices, fillColor, isSelected]
  );

  if (!vertices.length) return null;

  // Dimension helpers
  const widthMeters = bounds.width / 100 * (selectedMeasurementUnit?.conversionFactor || 1);
  const heightMeters = bounds.height / 100 * (selectedMeasurementUnit?.conversionFactor || 1);

  return (
    <Container ref={containerRef} interactive={true} pointerdown={handleMouseDown} pointermove={isDragging ? handleMouseMove : undefined} pointerup={handleMouseUp} >
      {/* Flooring pattern */}
      {(flooringPattern || pattern) && (
        <Graphics draw={drawFlooringPattern} interactive={false} />
      )}
      {/* Highlight overlay */}
      {highlightColor && (
        <Graphics draw={drawHighlight} interactive={false} />
      )}
      {/* Main polygon fill */}
      <Graphics draw={draw} interactive={true} pointerdown={onSelect} />
      {/* Room name */}
      <Text
        text={name}
        x={bounds.centerX}
        y={bounds.centerY - 18}
        anchor={0.5}
        style={{
          fontFamily: 'Arial',
          fontSize: 18,
          fill: isSelected ? 0x1976d2 : 0x333333,
          fontWeight: 'bold',
          align: 'center',
          dropShadow: true,
          dropShadowColor: 0xffffff,
          dropShadowBlur: 2,
          dropShadowAngle: Math.PI / 6,
          dropShadowDistance: 1,
          cursor: 'pointer',
        }}
        interactive={true}
        pointerdown={handleNameClick}
      />
      {/* Area display */}
      <Text
        text={`Area: ${area.toFixed(2)} m²`}
        x={bounds.centerX}
        y={bounds.centerY + 8}
        anchor={0.5}
        style={{
          fontFamily: 'Arial',
          fontSize: 14,
          fill: 0x666666,
          align: 'center',
        }}
        interactive={false}
      />
      {/* Square footage display */}
      {showSquareFootage && (
        <Text
          text={`${(area * 10.7639).toFixed(1)} sq ft`}
          x={bounds.centerX}
          y={bounds.centerY + 28}
          anchor={0.5}
          style={{
            fontFamily: 'Arial',
            fontSize: 12,
            fill: 0x000000,
            align: 'center',
            fontWeight: 'bold',
            dropShadow: true,
            dropShadowColor: 0xffffff,
            dropShadowBlur: 2,
            dropShadowAngle: Math.PI / 6,
            dropShadowDistance: 1,
          }}
        />
      )}
      {/* Inside dimensions */}
      {showInsideDimensions && (
        <>
          <Text
            text={`${widthMeters.toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
            x={bounds.centerX}
            y={bounds.minY + 10}
            anchor={0.5}
            style={{
              fontFamily: 'Arial',
              fontSize: 10,
              fill: 0x000000,
              align: 'center',
              fontWeight: 'bold',
              dropShadow: true,
              dropShadowColor: 0xffffff,
              dropShadowBlur: 1,
              dropShadowDistance: 1,
            }}
          />
          <Text
            text={`${heightMeters.toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
            x={bounds.maxX - 10}
            y={bounds.centerY}
            anchor={0.5}
            style={{
              fontFamily: 'Arial',
              fontSize: 10,
              fill: 0x000000,
              align: 'center',
              fontWeight: 'bold',
              dropShadow: true,
              dropShadowColor: 0xffffff,
              dropShadowBlur: 1,
              dropShadowDistance: 1,
            }}
          />
        </>
      )}
      {/* Outside dimensions */}
      {showOutsideDimensions && (
        <>
          <Text
            text={`${widthMeters.toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
            x={bounds.centerX}
            y={bounds.minY - 10}
            anchor={0.5}
            style={{
              fontFamily: 'Arial',
              fontSize: 10,
              fill: 0x666666,
              align: 'center',
              fontWeight: 'bold',
              dropShadow: true,
              dropShadowColor: 0xffffff,
              dropShadowBlur: 1,
              dropShadowDistance: 1,
            }}
          />
          <Text
            text={`${heightMeters.toFixed(1)}${selectedMeasurementUnit?.abbreviation || "'"}`}
            x={bounds.maxX + 20}
            y={bounds.centerY}
            anchor={0.5}
            style={{
              fontFamily: 'Arial',
              fontSize: 10,
              fill: 0x666666,
              align: 'center',
              fontWeight: 'bold',
              dropShadow: true,
              dropShadowColor: 0xffffff,
              dropShadowBlur: 1,
              dropShadowDistance: 1,
            }}
          />
        </>
      )}
      {/* Modifier mode indicator */}
      {isSelected && isModifierMode && (
        <Text
          text="MODIFIER MODE - Press M to exit"
          x={bounds.centerX}
          y={bounds.minY - 30}
          anchor={0.5}
          style={{
            fontFamily: 'Arial',
            fontSize: 12,
            fill: 0x00ff00,
            align: 'center',
            fontWeight: 'bold',
            dropShadow: true,
            dropShadowColor: 0x000000,
            dropShadowBlur: 2,
            dropShadowDistance: 1,
          }}
        />
      )}
    </Container>
  );
};

export default PolygonRoom; 